import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertCircle, Loader2, Pill, ShoppingCart, Boxes, ClipboardList } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Role configs drive the staff login experience. When the route bakes in a
// specific `role` prop (legacy /pharmacy/login, /purchase/login) the picker
// is hidden. The unified /staff/login URL leaves `role` undefined so the
// user picks from the dropdown — same backend endpoint, same data flow.
const ROLE_CONFIG = {
  pharmacy: {
    label: 'Pharmacy',
    title: 'Pharmacy Login',
    placeholder: 'e.g. pharmacy01',
    icon: Pill,
    accent: 'from-emerald-700 via-emerald-800 to-teal-900',
    btnColor: 'bg-emerald-700 hover:bg-emerald-800',
    redirect: '/pharmacy',
  },
  purchase: {
    label: 'Purchase',
    title: 'Purchase Login',
    placeholder: 'e.g. purchase01',
    icon: ShoppingCart,
    accent: 'from-blue-800 via-blue-900 to-indigo-900',
    btnColor: 'bg-blue-700 hover:bg-blue-800',
    redirect: '/purchase',
  },
  pharmacystores: {
    label: 'Pharmacy Stores',
    title: 'Pharmacy Stores Login',
    placeholder: 'e.g. pharmacystores01',
    icon: Boxes,
    accent: 'from-amber-700 via-orange-800 to-amber-900',
    btnColor: 'bg-amber-700 hover:bg-amber-800',
    redirect: '/pharmacy-stores',
  },
  storesadmin: {
    label: 'Stores Admin',
    title: 'Stores Admin Login',
    placeholder: 'e.g. storesadmin01',
    icon: ClipboardList,
    accent: 'from-purple-800 via-violet-900 to-fuchsia-900',
    btnColor: 'bg-purple-700 hover:bg-purple-800',
    redirect: '/stores-admin',
  },
};

const UNIFIED_DEFAULT = {
  label: 'Staff',
  title: 'Staff Login',
  icon: ClipboardList,
  accent: 'from-slate-800 via-slate-900 to-zinc-900',
  btnColor: 'bg-slate-800 hover:bg-slate-900',
};

export default function StaffLoginPage({ role: fixedRole }) {
  // When fixedRole is set, no picker. When undefined (the unified /staff/login)
  // the user picks the role first and we re-render with that config.
  const [selectedRole, setSelectedRole] = useState(fixedRole || 'pharmacystores');
  const activeRole = fixedRole || selectedRole;
  const cfg = ROLE_CONFIG[activeRole] || ROLE_CONFIG.pharmacy;
  // For the unified screen, keep the chrome neutral until the user picks.
  const chrome = fixedRole ? cfg : { ...UNIFIED_DEFAULT, ...cfg, accent: UNIFIED_DEFAULT.accent };
  const Icon = chrome.icon;
  const { setUser } = useAuth();
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('staff123');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/staff-login`, {
        user_id: userId.trim(),
        role: activeRole,
      }, { withCredentials: true });
      setUser(data);
      // Force the right landing page in case AuthContext / PublicRoute
      // hasn't propagated yet.
      window.location.assign(ROLE_CONFIG[data.role]?.redirect || '/');
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Login failed.');
    }
    setLoading(false);
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center bg-gradient-to-br ${chrome.accent} px-4`}
      data-testid={`${activeRole}-login-page`}
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="h-16 w-16 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center mx-auto mb-4">
            <Icon className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>ECare 360</h1>
          <p className="text-white/70 mt-2 text-base">{cfg.label} portal</p>
        </div>
        <Card className="shadow-2xl border-0">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl text-center" style={{ fontFamily: 'Manrope' }}>
              {fixedRole ? cfg.title : 'Staff Sign In'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div
                  className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
                  data-testid={`${activeRole}-login-error`}
                >
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />{error}
                </div>
              )}
              {!fixedRole && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Role</Label>
                  <Select value={selectedRole} onValueChange={setSelectedRole}>
                    <SelectTrigger className="h-12 text-base" data-testid="staff-login-role-picker">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pharmacy">Pharmacy</SelectItem>
                      <SelectItem value="purchase">Purchase</SelectItem>
                      <SelectItem value="pharmacystores">Pharmacy Stores</SelectItem>
                      <SelectItem value="storesadmin">Stores Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="staff-id" className="text-sm font-medium">{cfg.label} User ID</Label>
                <Input
                  id="staff-id"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  placeholder={cfg.placeholder}
                  className="h-12 text-base"
                  required
                  data-testid={`${activeRole}-login-user-id`}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="staff-password" className="text-sm font-medium">Password</Label>
                <Input
                  id="staff-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 text-base"
                  required
                  data-testid={`${activeRole}-login-password`}
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !userId.trim()}
                className={`w-full h-12 text-base ${chrome.btnColor}`}
                data-testid={`${activeRole}-login-submit`}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />Signing In...
                  </>
                ) : 'Sign In'}
              </Button>
              <p className="text-center text-xs text-gray-400 pt-2">
                Doctor? <a href="/login" className="text-blue-700 hover:underline">Log in here</a>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
